import { useTranslation } from 'react-i18next';
import {
  Button,
  CopyToClipboard,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import { useSearchParams } from 'react-router-dom';
import {
  CoordinatorShiftsPaginationPreferences,
  CoordinatorShiftsPaginationPreferencesProps,
} from './coordinatorEventShiftsTablePaginationPreference';
import Table from '@amzn/awsui-components-react/polaris/table';
import {
  AttendanceCompletion,
  DetailedShift,
  Event,
  EventDetails,
  GetShiftsByEventCommandOutput,
  Source,
  Shift,
  ShiftType,
} from '@amzn/red-velvet-api';
import {
  canManageGivehubEvent,
  canManageVCEvent,
  generateShiftActionLink,
  generateShiftNumberOfPeopleAttended,
  generateShiftNumberOfSignedUpInfo,
  generateShiftStartDateInfo,
  generateShiftStartTimeInfo,
  generateShiftTotals,
} from '../../utils/shiftUtils';
import { useTimezone } from '../../hooks/timezone';
import { SpaLink } from '../SpaLink';
import { useTableColumns } from '../../hooks/tableColumns';
import { useUser } from '../../hooks/user';
import { OpenEndedPagination, OpenEndedPaginationProps } from '../../hooks/openEndedPagination';
import { EditEventBenevityLink, ManageEventButton } from '../shifts/actions';
import {
  generateEventEndDateWithMonthAndDayToDisplay,
  generateEventStartDateWithMonthAndDayToDisplay,
} from '../../utils/eventUtils';
import {
  COORDINATOR_EVENT_SHIFTS_TABLE,
  SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE,
  SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE,
  TRACK_ATTENDANCE_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE,
  TRACK_ATTENDANCE_BULK_UPLOAD_MULTI_SHIFT,
  TRACK_ATTENDANCE_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE,
} from '../../utils/constants';
import { useAllPagesRedVelvetQuery } from '../../hooks/allPagesRedVelvetQuery';
import { useFeatures, featureIsEnabled } from '../../hooks/features';
import { calculateShiftTime } from '../../hooks/shifts/shiftTime';
import { useAttendeeSessionStorage } from '../../hooks/attendance/attendeeSessionStorage';
import { useParticipantSessionStorage } from '../../hooks/attendance/participantSessionStorage';

export const SHIFT_ATTENDANCE_STATUS_DISPLAY_MAP = {
  [AttendanceCompletion.UPCOMING]: {
    displayText: 'Upcoming',
    statusType: 'in-progress',
    color: 'grey',
  },
  [AttendanceCompletion.MISSING_ATTENDANCE]: {
    displayText: 'Missing attendance',
    statusType: 'warning',
    color: 'red',
  },
  [AttendanceCompletion.COMPLETED]: {
    displayText: 'Completed',
    statusType: 'success',
    color: 'green',
  },
};

const useShiftColumnMapForGivehubEvent = (
  selectedTimezone: string,
  urlFeatures: string | null,
  namespaces: string[] = ['translation'],
  fromEventDetailPage = false,
  event: Event,
  shifts: Shift[]
) => {
  const { t, i18n } = useTranslation(namespaces);
  const { resetAttendees } = useAttendeeSessionStorage();
  const { resetParticipants } = useParticipantSessionStorage();

  return {
    shiftName: (item: DetailedShift) => (item.shift.name ? item.shift.name : '-'),
    shiftStartDate: (item: DetailedShift) =>
      item.shift.shiftType === ShiftType.DROP_IN
        ? '-' // we should not display start date for Drop-in shift
        : generateShiftStartDateInfo(
            item.shift,
            selectedTimezone,
            i18n.language,
            t('coordinatorEventShiftsTable.onGoing')
          ),
    shiftStartTime: (item: DetailedShift) =>
      item.shift.shiftType === ShiftType.DROP_IN
        ? '-' // we should not display start time for Drop-in shift
        : generateShiftStartTimeInfo(
            item.shift,
            selectedTimezone,
            i18n.language,
            t('coordinatorEventShiftsTable.onGoing')
          ),
    shiftSignupNumber: (item: DetailedShift) => {
      const { numberOfGuests, signupCount } = generateShiftNumberOfSignedUpInfo(
        item.shift,
        item.signups
      );
      return numberOfGuests > 0
        ? t('coordinatorEventShiftsTable.numberOfSignupsWithGuests', {
            count: signupCount,
            guests: numberOfGuests,
          })
        : t('coordinatorEventShiftsTable.numberOfSignupsOnly', { count: signupCount });
    },
    shiftAttendeesNumber: (item: DetailedShift) =>
      generateShiftNumberOfPeopleAttended(item.attendees),
    shiftAttendanceTrackStatus: (item: DetailedShift) => {
      const status = item.attendanceCompletion as AttendanceCompletion | undefined;

      if (
        item.shift.name !== 'Waitlist' &&
        (!status || !SHIFT_ATTENDANCE_STATUS_DISPLAY_MAP[status])
      ) {
        return (
          <StatusIndicator type="error">
            {t('coordinatorEventShiftsTable.unknownShiftAttendanceStatus')}
          </StatusIndicator>
        );
      }
      if (!status) {
        return <></>;
      }
      const { displayText, statusType, color } = SHIFT_ATTENDANCE_STATUS_DISPLAY_MAP[status];
      return (
        <StatusIndicator
          type={statusType as 'success' | 'in-progress' | 'warning' | 'error'}
          colorOverride={color as 'green' | 'blue' | 'red' | 'grey'}
        >
          {displayText}
        </StatusIndicator>
      );
    },
    shiftActions: (item: DetailedShift) => (
      <SpaLink
        href={generateShiftActionLink(
          item.attendanceCompletion,
          item.shift.shiftId,
          item.shift.eventId,
          urlFeatures,
          'signup'
        )}
        data-aci-analytics-name={
          item.attendanceCompletion
            ? AttendanceCompletion.UPCOMING === item.attendanceCompletion
              ? fromEventDetailPage
                ? SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                : SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
              : fromEventDetailPage
                ? TRACK_ATTENDANCE_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                : TRACK_ATTENDANCE_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
            : ''
        }
        onFollow={() => {
          // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
          resetParticipants();
          resetAttendees();
        }}
      >
        {item.attendanceCompletion
          ? AttendanceCompletion.UPCOMING === item.attendanceCompletion
            ? t('coordinatorEventShiftsTable.signupAction')
            : t('coordinatorEventShiftsTable.trackAttendanceAction')
          : ''}
      </SpaLink>
    ),
  };
};

const useShiftColumnMapForUnlistedEvent = (
  selectedTimezone: string,
  urlFeatures: string | null,
  namespaces: string[] = ['translation'],
  fromEventDetailPage = false,
  event?: Event,
  shifts?: Shift[]
) => {
  const { t, i18n } = useTranslation(namespaces);

  return {
    shiftStartDate: (item: DetailedShift) =>
      generateShiftStartDateInfo(
        item.shift,
        selectedTimezone,
        i18n.language,
        t('coordinatorEventShiftsTable.onGoing')
      ),
    shiftLength: (item: DetailedShift) =>
      `${calculateShiftTime(item.shift)} ${t('coordinatorEventShiftsTable.shiftLength')}`,
    shiftAttendeesNumber: (item: DetailedShift) =>
      generateShiftNumberOfPeopleAttended(item.attendees),
    shiftActions: (item: DetailedShift) => (
      <SpaLink
        href={generateShiftActionLink(
          item.attendanceCompletion,
          item.shift.shiftId,
          item.shift.eventId,
          urlFeatures,
          'signup'
        )}
        data-aci-analytics-name={
          item.attendanceCompletion
            ? AttendanceCompletion.UPCOMING === item.attendanceCompletion
              ? fromEventDetailPage
                ? SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                : SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
              : fromEventDetailPage
                ? TRACK_ATTENDANCE_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                : TRACK_ATTENDANCE_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
            : ''
        }
      >
        {item.attendanceCompletion
          ? AttendanceCompletion.UPCOMING === item.attendanceCompletion
            ? t('coordinatorEventShiftsTable.signupAction')
            : t('coordinatorEventShiftsTable.trackAttendanceAction')
          : ''}
      </SpaLink>
    ),
  };
};

export function CoordinatorEventShiftsTable({
  urlFeatures,
  paginationProps,
  preferencesProps,
  eventDetail,
  fromEventDetailPage = false,
  isPastTab = false,
}: {
  urlFeatures: string | null;
  paginationProps: OpenEndedPaginationProps;
  preferencesProps?: CoordinatorShiftsPaginationPreferencesProps;
  eventDetail: EventDetails;
  fromEventDetailPage?: boolean;
  isPastTab?: boolean;
}) {
  const selectedTimezone = useTimezone();
  const shiftColumnMapForGivehubEvent = useShiftColumnMapForGivehubEvent(
    selectedTimezone,
    urlFeatures,
    ['activity', 'translation'],
    fromEventDetailPage,
    eventDetail.event,
    eventDetail.detailedShifts.map((ds) => ds.shift)
  );
  const shiftColumnMapForUnlistedEvent = useShiftColumnMapForUnlistedEvent(
    selectedTimezone,
    urlFeatures,
    ['activity', 'translation'],
    fromEventDetailPage,
    eventDetail.event,
    eventDetail.detailedShifts.map((ds) => ds.shift)
  );

  const { columnDefinitions } = useTableColumns(
    eventDetail.event.source === Source.GIVEHUB
      ? shiftColumnMapForGivehubEvent
      : shiftColumnMapForUnlistedEvent,
    'coordinatorEventShiftsTable',
    ['activity', 'translation']
  );
  return (
    <div>
      {eventDetail.detailedShifts.length !== 0 && (
        <Table
          header={
            <CoordinatorEventShiftsTableHeader
              eventDetails={eventDetail}
              urlFeatures={urlFeatures}
              selectedTimezone={selectedTimezone}
              fromEventDetailPage={fromEventDetailPage}
              isPastTab={isPastTab}
            />
          }
          columnDefinitions={columnDefinitions}
          stickyColumns={{ last: 1 }}
          pagination={<OpenEndedPagination {...paginationProps} />}
          preferences={<CoordinatorShiftsPaginationPreferences {...preferencesProps} />}
          items={eventDetail.detailedShifts}
          data-testid={'coordinator-activity-event-shifts-table'}
        />
      )}
    </div>
  );
}

interface CoordinatorEventShiftsTableHeaderProps {
  urlFeatures: string | null;
  selectedTimezone: string;
  eventDetails: EventDetails;
  fromEventDetailPage: boolean;
  isPastTab: boolean;
}

function CoordinatorEventShiftsTableHeader(props: CoordinatorEventShiftsTableHeaderProps) {
  const { t } = useTranslation(['activity', 'translation']);
  const { selectedTimezone, urlFeatures, eventDetails, fromEventDetailPage, isPastTab } = props;
  const user = useUser();
  const event: Event = eventDetails.event;
  const viewDetailsUrl = `/view-event?id=${event.eventId}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  const features = useFeatures();
  const [searchParams] = useSearchParams();

  const isBulkUploadMultiShiftEnabled = featureIsEnabled(
    TRACK_ATTENDANCE_BULK_UPLOAD_MULTI_SHIFT,
    features,
    searchParams
  );

  const shiftsQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getShiftsByEvent', { eventId: event.eventId }],
    converter: (output: GetShiftsByEventCommandOutput) => ({
      items: output.shifts || [],
      next: output.page,
    }),
  });
  const shiftLists = shiftsQuery.data || [];

  const editUnlistedEventUrl =
    shiftLists.length === 1 &&
    `/track-attendance?eventId=${event.eventId}&shiftId=${shiftLists[0].shiftId.split('shiftId-eventId-')[0]}`;
  const editGivehubEventUrl = `https://amazon.benevity.org/volunteer/${event.eventId}/edit`;

  const editEventAction = () => {
    return canManageGivehubEvent(event, user) ? (
      isPastTab && isBulkUploadMultiShiftEnabled ? (
        <ManageEventButton event={event} shifts={shiftLists} />
      ) : (
        <EditEventBenevityLink event={event} />
      )
    ) : canManageVCEvent(event, user) && editUnlistedEventUrl ? (
      <Button data-aci-analytics-name="coordinatorActivity_editEvent" href={editUnlistedEventUrl}>
        {t('coordinatorEventShiftsTable.actions.editEvent')}
      </Button>
    ) : undefined;
  };

  return (
    <Header
      variant={fromEventDetailPage ? 'h2' : 'h1'}
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          {editEventAction()}
          {!fromEventDetailPage && (
            <CopyToClipboard
              copyButtonText={t('coordinatorEventShiftsTable.actions.copyForGrantsCloseout')}
              copyErrorText={t('coordinatorEventShiftsTable.actions.copyForGrantsCloseoutFailed')}
              copySuccessText={t(
                'coordinatorEventShiftsTable.actions.copyForGrantsCloseoutSuccess'
              )}
              data-aci-analytics-name="coordinatorActivity_copyEventUrl"
              textToCopy={
                event.source === Source.VC
                  ? `https://www.volunteercenter.amazon.dev${editUnlistedEventUrl}`
                  : editGivehubEventUrl
              }
            />
          )}
        </SpaceBetween>
      }
      description={
        fromEventDetailPage ? null : (
          <CoordinatorEventShiftsTableHeaderDescription
            selectedTimezone={selectedTimezone}
            urlFeatures={urlFeatures}
            eventDetails={eventDetails}
            shiftLists={shiftLists}
          />
        )
      }
    >
      {fromEventDetailPage && t('coordinatorEventShiftsTable.tableTitle.shift')}
      {!fromEventDetailPage &&
        (event.source === Source.GIVEHUB ? (
          <SpaLink
            href={viewDetailsUrl}
            data-aci-analytics-name={COORDINATOR_EVENT_SHIFTS_TABLE + '_eventTitleLink'}
          >
            <h2 style={{ margin: 0 }}>{event.title}</h2>
          </SpaLink>
        ) : (
          <h4 style={{ margin: 0 }}>{event.title}</h4>
        ))}
    </Header>
  );
}

interface CoordinatorEventShiftsTableHeaderDescriptionProps {
  urlFeatures: string | null;
  selectedTimezone: string;
  eventDetails: EventDetails;
  shiftLists: Shift[];
}

function CoordinatorEventShiftsTableHeaderDescription(
  props: CoordinatorEventShiftsTableHeaderDescriptionProps
) {
  const { t, i18n } = useTranslation(['activity', 'translation']);
  const { selectedTimezone, urlFeatures, eventDetails, shiftLists } = props;
  const event: Event = eventDetails.event;

  const viewDetailsUrl = `/view-event?id=${event.eventId}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  const startDay = generateEventStartDateWithMonthAndDayToDisplay(
    selectedTimezone,
    i18n.language,
    event
  );
  const endDay = generateEventEndDateWithMonthAndDayToDisplay(
    selectedTimezone,
    i18n.language,
    event
  );

  const getDisplayLocation = (event: Event): string => {
    if (event.location.locationType === 'VIRTUAL') {
      return t('coordinatorEventShiftsTable.eventDisplay.virtualLocation');
    } else if (event.location.city && event.location.province && event.location.country) {
      return t('coordinatorEventShiftsTable.eventDisplay.physicalLocation', {
        city: event.location.city,
        state: event.location.province,
        country: event.location.country,
      });
    } else if (event.location.amazonBuilding) {
      return event.location.amazonBuilding;
    } else if (event.location.address_string) {
      return event.location.address_string;
    } else return '';
  };

  const displayedLocation = getDisplayLocation(event);
  const { totalSpots, totalShifts } = generateShiftTotals(shiftLists);
  const hasUnlimitedSpots = totalSpots === Infinity;
  return (
    <span>
      <span>
        <small className={'coordinator-table-event-header-none-description'}>
          {startDay}
          {endDay ? ` - ${endDay}` : ''} | {displayedLocation}
        </small>
        <small className={'coordinator-table-event-header-none-description'}>
          {hasUnlimitedSpots
            ? t('coordinatorEventShiftsTable.eventDisplay.unlimited')
            : totalSpots <= 1
              ? t('coordinatorEventShiftsTable.eventDisplay.totalSpots_Less_Or_Equal_One', {
                  count: totalSpots,
                })
              : t('coordinatorEventShiftsTable.eventDisplay.totalSpots_More_Than_One', {
                  count: totalSpots,
                })}{' '}
          {totalShifts <= 1
            ? t('coordinatorEventShiftsTable.eventDisplay.totalShifts_Less_Or_Equal_One', {
                count: totalShifts,
              })
            : t('coordinatorEventShiftsTable.eventDisplay.totalShifts_More_Than_One', {
                count: totalShifts,
              })}
        </small>
      </span>
      {event.cause && (
        <small className={'coordinator-table-event-header-organization'}>
          {t('coordinatorEventShiftsTable.eventDisplay.organization', {
            organization: event.cause,
          })}
        </small>
      )}
      <span className={'coordinator-table-event-header-description'}>
        {event.summary && event.summary.length > 180
          ? `${event.summary.slice(0, 180)}...`
          : event.summary}
        {'\t'}
        {event.source === Source.GIVEHUB && (
          <SpaLink
            href={viewDetailsUrl}
            data-aci-analytics-name={COORDINATOR_EVENT_SHIFTS_TABLE + '_readMoreLink'}
          >
            {t('coordinatorEventShiftsTable.eventDisplay.readMore')}
          </SpaLink>
        )}
      </span>
    </span>
  );
}
